import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import clsx from 'clsx';
import {
  ArrowUp,
  Check,
  X,
  PauseCircle,
  Archive as ArchiveIcon,
  Paperclip,
} from 'lucide-react';
import type { FactoryRun, StageAction, ThreadMessage } from '@/lib/types';
import {
  STAGE_LABELS,
  AWAITING_APPROVAL_STATUSES,
} from '@/lib/types';
import { useThreadMessages } from './hooks/useThreadMessages';
import { useCustomerName } from './hooks/useCustomerName';
import { stubSendFounderMessage, stubStageAction } from './actions';

interface Props {
  run: FactoryRun | null;
}

// Right pane — the active thread: header, message body, founder input.
// Messages are bubble-style (founder on right in accent color; agents on
// left in neutral). Stage transitions render as inline system pills.
export function ActiveThread({ run }: Props) {
  const { messages, isMock, loading, error } = useThreadMessages(run?.id ?? null);
  const displayName = useCustomerName(run?.customerId, run?.customerName);

  if (!run) return <EmptyState />;

  return (
    <section
      aria-label="Active thread"
      className="flex h-full flex-1 flex-col bg-canvas-50/40"
    >
      <ThreadHeader run={run} displayName={displayName} />
      <ThreadBody
        messages={messages}
        loading={loading}
        isMock={isMock}
        error={error}
      />
      <FounderInput runId={run.id} />
    </section>
  );
}

function ThreadHeader({
  run,
  displayName,
}: {
  run: FactoryRun;
  displayName: string;
}) {
  const awaitingApproval =
    run.awaitingApproval || AWAITING_APPROVAL_STATUSES.includes(run.status);

  const doAction = (action: StageAction) => stubStageAction(action, run);

  return (
    <header className="flex items-center justify-between gap-4 border-b border-ink-100 bg-white/70 px-6 py-4 backdrop-blur">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <StatusDot
            status={
              awaitingApproval
                ? 'awaiting'
                : run.status === 'paused'
                  ? 'paused'
                  : run.status === 'complete'
                    ? 'complete'
                    : 'active'
            }
          />
          <h1 className="truncate text-base font-semibold text-ink-900">{displayName}</h1>
          <span className="rounded bg-ink-100/60 px-1.5 py-px text-[10px] font-medium uppercase tracking-wider text-ink-600">
            {STAGE_LABELS[run.currentStage]}
          </span>
          {awaitingApproval && (
            <span className="rounded bg-amber-100 px-1.5 py-px text-[10px] font-medium uppercase tracking-wider text-amber-900">
              needs approval
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-ink-400">
          <span className="font-mono">{run.id}</span>
          {run.userEmail && <span>· {run.userEmail}</span>}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <ActionButton
          label="Approve"
          tone="approve"
          disabled={!awaitingApproval}
          title={
            awaitingApproval
              ? 'Approve the current stage and advance'
              : 'No stage awaiting approval'
          }
          onClick={() => doAction('approve')}
        >
          <Check size={14} />
        </ActionButton>
        <ActionButton
          label="Reject"
          tone="reject"
          disabled={!awaitingApproval}
          onClick={() => doAction('reject')}
        >
          <X size={14} />
        </ActionButton>
        <ActionButton
          label="Pause"
          tone="muted"
          onClick={() => doAction('pause')}
          disabled={run.status === 'paused' || run.status === 'complete'}
        >
          <PauseCircle size={14} />
        </ActionButton>
        <ActionButton label="Archive" tone="muted" onClick={() => doAction('archive')}>
          <ArchiveIcon size={14} />
        </ActionButton>
      </div>
    </header>
  );
}

function ThreadBody({
  messages,
  loading,
  isMock,
  error,
}: {
  messages: ThreadMessage[];
  loading: boolean;
  isMock: boolean;
  error: string | null;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const initialRender = useRef(true);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: initialRender.current ? 'auto' : 'smooth',
    });
    initialRender.current = false;
  }, [messages.length]);

  useEffect(() => {
    const t = setInterval(
      () => ref.current?.setAttribute('data-tick', String(Date.now())),
      30_000,
    );
    return () => clearInterval(t);
  }, []);

  return (
    <div ref={ref} className="flex-1 min-h-0 overflow-y-auto scrollbar-thin px-6 py-4">
      {isMock && messages.length > 0 && (
        <div className="mx-auto mb-3 w-fit rounded-full border border-white/70 bg-white/60 px-3 py-0.5 text-[10px] uppercase tracking-wider text-ink-400">
          mock conversation — no live run connected
        </div>
      )}
      {error && (
        <div className="mx-auto mb-3 w-fit rounded-full border border-rose-200 bg-rose-50/70 px-3 py-0.5 text-[10px] text-rose-800">
          subscription error: {error}
        </div>
      )}
      {loading && messages.length === 0 && (
        <div className="pt-10 text-center text-xs text-ink-400">Loading messages…</div>
      )}
      {!loading && messages.length === 0 && (
        <div className="pt-10 text-center text-xs text-ink-400">
          No messages in this thread yet.
        </div>
      )}
      <div className="space-y-3">
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: ThreadMessage }) {
  if (message.isStageTransition || message.sender === 'System') {
    return (
      <div className="mx-auto w-fit rounded-full border border-white/70 bg-white/50 px-3 py-0.5 text-[11px] text-ink-500">
        {message.text}
      </div>
    );
  }
  const isFounder = message.sender === 'Founder';
  return (
    <div
      className={clsx(
        'flex animate-fade-in',
        isFounder ? 'justify-end' : 'justify-start',
      )}
    >
      <div
        className={clsx(
          'max-w-[82%] whitespace-pre-wrap break-words px-4 py-2 text-sm leading-relaxed',
          isFounder
            ? 'rounded-2xl rounded-tr-sm bg-accent-100 text-ink-900'
            : 'rounded-2xl rounded-tl-sm border border-white/70 bg-white/80 text-ink-800 shadow-glass-sm',
        )}
      >
        {!isFounder && (
          <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-700">
            {message.sender}
          </div>
        )}
        <div>{message.text}</div>
        <div className="mt-1 text-[10px] text-ink-400">
          {new Date(message.createdAtMs).toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
          })}
        </div>
      </div>
    </div>
  );
}

function FounderInput({ runId }: { runId: string }) {
  const [text, setText] = useState('');

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    stubSendFounderMessage(runId, trimmed);
    setText('');
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <form
      onSubmit={submit}
      className="m-4 flex items-end gap-2 rounded-2xl border border-white/70 bg-white/80 p-2 shadow-glass-sm"
    >
      <IconButton
        title="Attach (stub)"
        onClick={() => console.log('[stub] founder-input attach clicked')}
      >
        <Paperclip size={16} />
      </IconButton>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKey}
        placeholder="Reply to this run…"
        rows={1}
        className="max-h-40 flex-1 resize-none bg-transparent px-2 py-1.5 text-sm leading-relaxed outline-none placeholder:text-ink-400"
      />
      <button
        type="submit"
        disabled={!text.trim()}
        aria-label="Send"
        className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-500 text-white transition hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ArrowUp size={16} />
      </button>
    </form>
  );
}

function IconButton({
  title,
  onClick,
  children,
}: {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="flex h-9 w-9 items-center justify-center rounded-xl text-ink-600 transition hover:bg-white"
    >
      {children}
    </button>
  );
}

function ActionButton({
  label,
  tone,
  onClick,
  disabled,
  title,
  children,
}: {
  label: string;
  tone: 'approve' | 'reject' | 'muted';
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  children?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title ?? label}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-40',
        tone === 'approve' &&
          'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100',
        tone === 'reject' &&
          'border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100',
        tone === 'muted' &&
          'border-ink-100 bg-white text-ink-700 hover:border-ink-200',
      )}
    >
      {children}
      <span>{label}</span>
    </button>
  );
}

function StatusDot({ status }: { status: 'active' | 'awaiting' | 'paused' | 'complete' }) {
  const color =
    status === 'active'
      ? 'bg-status-active'
      : status === 'awaiting'
        ? 'bg-status-awaiting animate-pulse-soft'
        : status === 'paused'
          ? 'bg-status-paused'
          : 'bg-status-complete';
  return <span className={clsx('h-2.5 w-2.5 rounded-full', color)} aria-hidden />;
}

function EmptyState() {
  return (
    <section className="flex h-full flex-1 items-center justify-center bg-canvas-50/40">
      <div className="glass-sm max-w-md p-8 text-center">
        <div className="text-xs uppercase tracking-[0.18em] text-ink-400">
          No thread selected
        </div>
        <h2 className="mt-2 text-lg font-semibold text-ink-900">Pick a run on the left</h2>
        <p className="mt-2 text-sm text-ink-600">
          Every Factory run is a thread. Pinned items are waiting on your approval — open one
          to review the agent conversation and advance or pause the run.
        </p>
      </div>
    </section>
  );
}
